// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// The one page-level tab bar. Hand-rolled rather than Carbon <Tabs> so the
// underline shelf runs edge-to-edge and the count pills match the operator
// console; keeps tab/tablist ARIA roles and arrow-key roving focus.
//
// Render this OUTSIDE `.hs-page` / directly inside `.hs-screen`: the bar is
// flush with the icon rail's right border, and its 8px top pad plus 44px tab
// height land the bottom border exactly on the bottom edge of the rail's Home
// item, so the two borders meet in an L.

import React, { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Persona } from '../../stores/shellStore';

export interface PageTab {
  key: string;
  label: string;
  /** Absolute path to navigate to and match against. */
  to: string;
  /**
   * Query string (with the leading `?`) appended when navigating to the tab,
   * so a section's URL state survives a round trip through its siblings.
   * Never part of the active match, which is on the pathname only.
   */
  search?: string;
  /**
   * When true, only an exact pathname match activates this tab.
   * Use for root-level paths (e.g. `/log`) that would otherwise
   * incorrectly match sub-routes.
   */
  exact?: boolean;
  /** Rendered as a pill when non-null; omit for tabs that carry no count. */
  count?: number | null;
  /** Count pill foreground; defaults to a neutral secondary text color. */
  countFg?: string;
  /** Count pill background; defaults to a neutral subtle border color. */
  countBg?: string;
  /** Caps the pill at `${countMax}+`; omit to always show the true count. */
  countMax?: number;
  /**
   * When set, the tab is only rendered for personas in this list.
   * Omit to show the tab to all personas.
   */
  roles?: Persona[];
}

export interface PageTabsProps {
  tabs: PageTab[];
  ariaLabel: string;
  /**
   * Overrides the route-derived selection. Use when the active tab is not a
   * plain pathname match — e.g. Home maps several `/home/:bucket` values onto
   * one tab. Leave unset and the tab whose `to` matches the current pathname
   * (exactly, or as a sub-route prefix unless `exact`) is selected.
   */
  activeKey?: string;
  /**
   * Active persona. When provided, tabs with a `roles` filter are shown only
   * if the persona is in the list. Tabs without a `roles` field are always shown.
   */
  persona?: Persona;
  /**
   * When set, each tab gets `data-testid={`${testIdPrefix}-${tab.key}`}`.
   * Prefer these over the accessible name in tests: the count pill sits inside
   * the button, so the name is "Alarms 12" rather than "Alarms", and labels get
   * renamed far more often than keys do.
   */
  testIdPrefix?: string;
  /** Right-aligned slot on the tab bar row. */
  actions?: React.ReactNode;
}

const hrefOf = (tab: PageTab) => `${tab.to}${tab.search ?? ''}`;

/**
 * Generic route-aware page tab bar.
 *
 * ```tsx
 * const BILLS_TABS: PageTab[] = [
 *   { key: 'overview', label: 'Overview', to: '/bills/overview' },
 *   { key: 'invoices', label: 'Invoices', to: '/bills/invoices', count: 3 },
 * ];
 * <PageTabs tabs={BILLS_TABS} ariaLabel="Bills" />
 * ```
 */
export function PageTabs({
  tabs,
  ariaLabel,
  activeKey,
  persona,
  testIdPrefix,
  actions,
}: PageTabsProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const visibleTabs = persona ? tabs.filter(t => !t.roles || t.roles.includes(persona)) : tabs;

  const activeIndex = Math.max(
    0,
    activeKey != null
      ? visibleTabs.findIndex(t => t.key === activeKey)
      : visibleTabs.findIndex(
          t => location.pathname === t.to || (!t.exact && location.pathname.startsWith(`${t.to}/`))
        )
  );

  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const n = visibleTabs.length;
    const next = e.key === 'ArrowRight' ? (i + 1) % n : (i - 1 + n) % n;
    refs.current[next]?.focus();
    navigate(hrefOf(visibleTabs[next]));
  };

  return (
    <nav className="hs-page-tabs" role="tablist" aria-label={ariaLabel}>
      {visibleTabs.map((tab, i) => {
        const isActive = i === activeIndex;
        return (
          <button
            key={tab.key}
            ref={el => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            data-testid={testIdPrefix ? `${testIdPrefix}-${tab.key}` : undefined}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            className={`hs-page-tab${isActive ? ' active' : ''}`}
            onClick={() => navigate(hrefOf(tab))}
            onKeyDown={e => onKeyDown(e, i)}
          >
            {tab.label}
            {tab.count != null && (
              <span
                className="hs-page-tab-count"
                data-testid={testIdPrefix ? `${testIdPrefix}-${tab.key}-count` : undefined}
                style={{
                  color: tab.countFg ?? 'var(--text-secondary)',
                  background: tab.countBg ?? 'var(--border-subtle)',
                }}
              >
                {tab.countMax != null && tab.count > tab.countMax ? `${tab.countMax}+` : tab.count}
              </span>
            )}
          </button>
        );
      })}
      {actions && <div className="hs-page-tabs-actions">{actions}</div>}
    </nav>
  );
}

export default PageTabs;
